import React, { ReactElement, useState } from "react";
import { useDispatch } from "react-redux";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomButton from "components/common/Button";
import { AlertType } from "components/common/Snackbar";

import { showSnackbar } from "store/actions/Utility";

import { makeRequest } from "util/APIHelper";
import { baseUrl } from "util/APIBaseUrl";

import "components/Widgets/ResumeSubscriptionDialog.scss";

const RESUME_DIALOG_TITLE = "Resume subscription?";
const RESUME_DIALOG_BODY =
  "This customer is active again, but their subscription is still paused. Resume billing now, or keep it paused and resume it later from the billing dashboard.";
const RESUME_SUCCESS_MESSAGE = "Subscription resumed.";
const RESUME_FALLBACK_ERROR = "Could not resume the subscription. Please try again.";

interface ResumeSubscriptionDialogProps {
  open: boolean;
  customerId: string | null;
  onClose: () => void;
}

interface ResumeSubscriptionResponse {
  ok: boolean;
  status?: number;
  payload?: {
    success?: boolean;
    message?: string;
    error?: Array<{ description?: string }>;
  };
}

const getResumeErrorMessage = (response?: ResumeSubscriptionResponse): string =>
  response?.payload?.message || response?.payload?.error?.[0]?.description || RESUME_FALLBACK_ERROR;

function ResumeSubscriptionDialog({ open, customerId, onClose }: ResumeSubscriptionDialogProps): ReactElement {
  const dispatch = useDispatch();

  const [isResuming, setIsResuming] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleClose = (): void => {
    if (isResuming) return;
    setErrorMessage("");
    onClose();
  };

  const handleResume = async (): Promise<void> => {
    if (!customerId) return;

    setIsResuming(true);
    setErrorMessage("");

    let response: ResumeSubscriptionResponse | undefined;
    try {
      response = await makeRequest({
        path: `${baseUrl}/customer/${customerId}/billing-subscription/resume`,
        method: "POST",
        contentType: "application/json",
        body: JSON.stringify({}),
      });
    } catch {
      response = undefined;
    }

    setIsResuming(false);

    if (response?.ok && response?.payload?.success !== false) {
      dispatch(showSnackbar({ type: AlertType.success, message: RESUME_SUCCESS_MESSAGE, dispatch }));
      onClose();
      return;
    }

    setErrorMessage(getResumeErrorMessage(response));
  };

  const dialogTitle = <DialogTitle title={RESUME_DIALOG_TITLE} showCloseIcon onClose={handleClose} />;

  const dialogContent = (
    <DialogContent>
      <p className="para-body-m-regular color-text-body-primary">{RESUME_DIALOG_BODY}</p>
      {errorMessage && (
        <p className="para-body-s-regular resume-subscription-dialog-error" role="alert">
          {errorMessage}
        </p>
      )}
    </DialogContent>
  );

  const dialogActions = (
    <DialogActions
      action1={
        <CustomButton
          size="large"
          label="Keep paused"
          onClick={handleClose}
          variant="contained"
          color="primary"
          isDisabled={isResuming}
        />
      }
      action2={
        <CustomButton
          size="large"
          label="Resume subscription"
          onClick={handleResume}
          variant="contained"
          color="primary"
          isLoading={isResuming}
        />
      }
    />
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      size="xs"
      fullWidth={false}
      title={dialogTitle}
      content={dialogContent}
      actions={dialogActions}
    />
  );
}

export default ResumeSubscriptionDialog;
